import { useCallback } from "react";
import { getMaster } from "utils/api";
import {
  createOptionsCacheHook,
  resolveHasMoreOptions,
} from "./globalOptionsCache";

const PAGE_SIZE = 500;
const FULL_LIST_CACHE_NAMESPACE = "role-options-full-list";

const buildRoleOption = (role) => {
  const id = `${role?.id ?? ""}`.trim();
  const name = `${role?.name ?? role?.title ?? ""}`.trim();

  if (!id) return null;

  return {
    key: id,
    value: id,
    title: name,
  };
};

const filterRolesForUser = (roles, currentUserRole) => {
  if (currentUserRole === "SUPER_ADMIN") {
    return roles;
  }

  return roles.filter(
    (role) => `${role?.name ?? role?.title ?? ""}` !== "SUPER_ADMIN",
  );
};

const fetchAllRoleOptionsFromApi = async (currentUserRole = "") => {
  try {
    const apiResponse = await getMaster("/roles", {
      sort: "id,asc",
      page: 0,
      size: PAGE_SIZE,
    });

    const responseBody = apiResponse?.data;
    if (!responseBody || responseBody.responseCode !== "0000") {
      return { options: [], hasMoreOptions: false };
    }

    const nextOptions = filterRolesForUser(
      responseBody.data?.roleList || [],
      currentUserRole,
    )
      .map(buildRoleOption)
      .filter(Boolean);

    return {
      options: nextOptions,
      hasMoreOptions: resolveHasMoreOptions(
        responseBody?.total,
        nextOptions.length,
        PAGE_SIZE,
      ),
    };
  } catch (error) {
    return { options: [], hasMoreOptions: false };
  }
};

const useAllRoleOptionsCache = createOptionsCacheHook({
  namespace: FULL_LIST_CACHE_NAMESPACE,
  buildCacheKey: (currentUserRole = "") => currentUserRole,
  fetcher: (currentUserRole = "") => fetchAllRoleOptionsFromApi(currentUserRole),
  fetchProperty: "fetchAllRoleOptions",
  invalidateProperty: "invalidateRoleOptionsCache",
});

export const useRoleOptionsCache = () => {
  const { fetchAllRoleOptions, invalidateRoleOptionsCache } =
    useAllRoleOptionsCache();

  const fetchRoleOptions = useCallback(
    async (inputValue = "", currentUserRole = "") => {
      const { options } = await fetchAllRoleOptions(currentUserRole);
      const keyword = `${inputValue || ""}`.trim().toLowerCase();

      return {
        options: keyword
          ? options.filter((option) =>
              option.title.toLowerCase().includes(keyword) ||
              `${option.value}`.toLowerCase().includes(keyword),
            )
          : options,
        hasMoreOptions: false,
      };
    },
    [fetchAllRoleOptions],
  );

  return {
    fetchRoleOptions,
    invalidateRoleOptionsCache,
  };
};
